import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InformationCircleIcon, PlusIcon, PencilSquareIcon, TrashIcon, Cog6ToothIcon, UserIcon, CurrencyDollarIcon } from '@heroicons/react/20/solid';
import mapboxgl from 'mapbox-gl';
import { AppContext } from '../context/AppContext';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DynamicForm from './DynamicForm';
import Overlay from './Overlay';
import { DataTable } from 'simple-datatables';
import 'simple-datatables/dist/style.css';
import Toggle from './Toggle';
import ContactSearchModal from './ContactSearchModal';
import SaleSearchModal from './SaleSearchModal';
import PlotAttachments from './PlotAttachments';
import FlexFieldValues from './FlexFieldValues';
import DocumentGenerationPanel from './DocumentGenerationPanel';

const MAPBOX_DEFAULT_TOKEN = 'pk.eyJ1Ijoic2hhd25jcmFpbmUiLCJhIjoiY2xzN3Z5bmhwMjhzZTJrbnE5MGZ6NTI3YSJ9.K3ZOPNCbtyu6KYEtVO2jkQ';
mapboxgl.accessToken = (process.env.REACT_APP_MAPBOX_TOKEN && process.env.REACT_APP_MAPBOX_TOKEN.trim()) || MAPBOX_DEFAULT_TOKEN;

// Helper to validate if coordinates are within WGS84 bounds
const isValidCoordinate = (coord) => {
  if (!Array.isArray(coord) || coord.length < 2) return false;
  const [lng, lat] = coord;
  return typeof lng === 'number' && typeof lat === 'number' &&
         lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
};

const PlotMap = ({ showHelp = false, showTable = false, previewMode = true }) => {
  const { plotColors, token, showPlotSize, user } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [map, setMap] = useState(null);
  const [draw, setDraw] = useState(null);
  const [plots, setPlots] = useState([]);
  const [invalidPlots, setInvalidPlots] = useState([]);
  const [showInvalidDialog, setShowInvalidDialog] = useState(false);
  const [isDeletingInvalid, setIsDeletingInvalid] = useState(false);
  const [allPlotContacts, setAllPlotContacts] = useState([]);
  const [highlightedPlotId, setHighlightedPlotId] = useState(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [formConfig, setFormConfig] = useState(null);
  const [currentPlot, setCurrentPlot] = useState(null);
  const [syncWithMap, setSyncWithMap] = useState(() => {
    // Retrieve from localStorage, default to true
    const stored = localStorage.getItem('plotMapSyncWithMap');
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [mapBounds, setMapBounds] = useState(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [pendingGeometry, setPendingGeometry] = useState(null);
  const [mapReady, setMapReady] = useState(false); // Track when map style is fully loaded
  const [filteredCount, setFilteredCount] = useState(0); // Track filtered plots count
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [managePlot, setManagePlot] = useState(null);
  const [plotContacts, setPlotContacts] = useState([]);
  const [plotSales, setPlotSales] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [relationshipType, setRelationshipType] = useState('owner');
  const [plotAttachments, setPlotAttachments] = useState([]);
  const popupRef = useRef(null);
  const mapRef = useRef(null);
  const plotsRef = useRef([]); // Ref to access current plots in event handlers
  const dataTableRef = useRef(null); // Ref to DataTable instance for search handling
  const drawRef = useRef(null); // Ref to access draw control in event handlers
  const hasHandledHighlightRef = useRef(false); // Track if we've handled a highlight parameter
  const showPlotSizeRef = useRef(false); // Ref to access showPlotSize in event handlers
  const searchTermRef = useRef(''); // Preserve search term across DataTable re-renders
  const isRestoringSearchRef = useRef(false); // Flag to prevent infinite loop when restoring search
  const searchFilteredPlotIdsRef = useRef(null); // Track search-filtered plot IDs for map (ref to avoid re-renders)

  // Keep refs in sync with state
  useEffect(() => {
    plotsRef.current = plots;
  }, [plots]);

  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);

  useEffect(() => {
    showPlotSizeRef.current = showPlotSize;
  }, [showPlotSize]);

  const debug = (msg, data = null) => {
    if (data) console.log(msg, data);
    else console.log(msg);
  };

  // Calculate approximate area of a polygon in square feet
  // Uses the Shoelace formula with coordinate conversion
  const calculatePlotArea = useCallback((geometry) => {
    if (!geometry || !geometry.coordinates) return null;

    const calculateRingArea = (ring) => {
      if (!ring || ring.length < 3) return 0;

      // Convert lat/lng to approximate feet (at mid-latitude)
      // 1 degree latitude ≈ 364,000 feet
      // 1 degree longitude varies by latitude, use cosine correction
      const midLat = ring.reduce((sum, coord) => sum + coord[1], 0) / ring.length;
      const latFeetPerDegree = 364000;
      const lngFeetPerDegree = 364000 * Math.cos(midLat * Math.PI / 180);

      // Convert coordinates to feet
      const feetCoords = ring.map(coord => [
        coord[0] * lngFeetPerDegree,
        coord[1] * latFeetPerDegree
      ]);

      // Shoelace formula
      let area = 0;
      for (let i = 0; i < feetCoords.length - 1; i++) {
        area += feetCoords[i][0] * feetCoords[i + 1][1];
        area -= feetCoords[i + 1][0] * feetCoords[i][1];
      }
      return Math.abs(area / 2);
    };

    let totalArea = 0;
    if (geometry.type === 'Polygon') {
      // First ring is outer boundary, subsequent rings are holes
      totalArea = calculateRingArea(geometry.coordinates[0]);
      for (let i = 1; i < geometry.coordinates.length; i++) {
        totalArea -= calculateRingArea(geometry.coordinates[i]);
      }
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygon => {
        let polygonArea = calculateRingArea(polygon[0]);
        for (let i = 1; i < polygon.length; i++) {
          polygonArea -= calculateRingArea(polygon[i]);
        }
        totalArea += polygonArea;
      });
    }

    return totalArea > 0 ? totalArea.toFixed(1) : null;
  }, []);

  const zoomToPlots = useCallback(() => {
    debug('Zooming to plots');
    if (map && plots.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      const invalid = [];

      plots.forEach(plot => {
        if (plot.geometry && plot.geometry.coordinates) {
          const coords = plot.geometry.coordinates;
          let hasInvalidCoords = false;

          const processCoord = (coord) => {
            if (isValidCoordinate(coord)) {
              bounds.extend(coord);
            } else {
              hasInvalidCoords = true;
            }
          };

          try {
            if (plot.geometry.type === 'MultiPolygon') {
              coords.forEach(polygon => {
                polygon.forEach(ring => {
                  ring.forEach(processCoord);
                });
              });
            } else if (plot.geometry.type === 'Polygon') {
              coords.forEach(ring => {
                ring.forEach(processCoord);
              });
            }
          } catch (e) {
            hasInvalidCoords = true;
          }

          if (hasInvalidCoords) {
            invalid.push(plot);
          }
        }
      });

      if (invalid.length > 0) {
        setInvalidPlots(invalid);
        if (user?.role === 'admin') {
          setShowInvalidDialog(true);
        }
      }

      if (!bounds.isEmpty()) {
        setMapBounds(bounds);
        map.fitBounds(bounds, { padding: 100, duration: 800 });
      }
    }
  }, [map, plots]);

  // Zoom to a specific plot by ID
  const zoomToPlot = useCallback((plotId) => {
    debug('Zooming to plot:', plotId);
    if (!map || !plots.length) return;

    const plot = plots.find(p => String(p.fid) === String(plotId));
    if (!plot || !plot.geometry || !plot.geometry.coordinates) {
      debug('Plot not found or has no geometry:', plotId);
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    const coords = plot.geometry.coordinates;

    if (plot.geometry.type === 'MultiPolygon') {
      coords.forEach(polygon => {
        polygon.forEach(ring => {
          ring.forEach(coord => {
            bounds.extend(coord);
          });
        });
      });
    } else if (plot.geometry.type === 'Polygon') {
      coords.forEach(ring => {
        ring.forEach(coord => {
          bounds.extend(coord);
        });
      });
    }

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 150, duration: 1000, maxZoom: 20 });
    }
  }, [map, plots]);

  // Helper function to determine plot state based on contacts
  const getPlotState = useCallback((plotId) => {
    const contacts = allPlotContacts.filter(pc => String(pc.plotId) === String(plotId));
    if (contacts.length === 0) {
      return 'available';
    }
    const hasInterred = contacts.some(pc => pc.relationshipType === 'interred');
    if (hasInterred) {
      return 'interred';
    }
    return 'sold';
  }, [allPlotContacts]);

  // Helper function to get contact names by relationship type for a plot
  const getContactNamesByType = useCallback((plotId, types) => {
    const contacts = allPlotContacts.filter(pc =>
      String(pc.plotId) === String(plotId) && types.includes(pc.relationshipType)
    );
    return contacts
      .map(pc => `${pc.Contact?.firstName || ''} ${pc.Contact?.lastName || ''}`.trim())
      .filter(name => name)
      .join(', ');
  }, [allPlotContacts]);

  // Zoom to extent of specific plots
  const zoomToFilteredPlots = useCallback((plotIds) => {
    if (!map || !plotIds || plotIds.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    let hasValidBounds = false;

    plotIds.forEach(plotId => {
      const plot = plots.find(p => String(p.fid) === String(plotId));
      if (!plot || !plot.geometry || !plot.geometry.coordinates) return;

      const coords = plot.geometry.coordinates;
      if (plot.geometry.type === 'MultiPolygon') {
        coords.forEach(polygon => {
          polygon.forEach(ring => {
            ring.forEach(coord => {
              bounds.extend(coord);
              hasValidBounds = true;
            });
          });
        });
      } else if (plot.geometry.type === 'Polygon') {
        coords.forEach(ring => {
          ring.forEach(coord => {
            bounds.extend(coord);
            hasValidBounds = true;
          });
        });
      }
    });

    if (hasValidBounds && !bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 100, duration: 800 });
    }
  }, [map, plots]);

  const handleDelete = async (plot) => {
    if (!window.confirm(`Are you sure you want to delete plot "${plot.alias}"?`)) return;
    try {
      const response = await fetch(`/api/plots/${plot.fid}`, { method: 'DELETE' });
      if (response.ok) {
        setPlots((prev) => prev.filter((p) => p.fid !== plot.fid));
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const renderPolygons = useCallback(() => {
    if (!map || !plots.length) return;
    debug(`Rendering ${plots.length} polygons`);

    // Remove existing layers and sources if they exist
    if (map.getLayer('plots-fill')) map.removeLayer('plots-fill');
    if (map.getLayer('plots-outline')) map.removeLayer('plots-outline');
    if (map.getLayer('plots-labels')) map.removeLayer('plots-labels');
    if (map.getSource('plots')) map.removeSource('plots');

    // Filter plots with valid geometry
    let validPlots = plots.filter(plot => {
      if (!plot.geometry) {
        debug(`Plot ${plot.fid} has no geometry`);
        return false;
      }
      if (!plot.geometry.type || !plot.geometry.coordinates) {
        debug(`Plot ${plot.fid} has invalid geometry structure`, plot.geometry);
        return false;
      }
      if (!Array.isArray(plot.geometry.coordinates) || plot.geometry.coordinates.length === 0) {
        debug(`Plot ${plot.fid} has empty coordinates`);
        return false;
      }
      return true;
    });

    debug(`Valid plots for rendering: ${validPlots.length}`, validPlots.length > 0 ? validPlots[0] : 'none');

    if (validPlots.length === 0) {
      debug('No valid plots to render');
      return;
    }

    // Helper to get plot state based on contacts and reserved status
    const getPlotStateForRender = (plot) => {
      // Check if plot is reserved first
      if (plot.isReserved) return 'reserved';

      const contacts = allPlotContacts.filter(pc => String(pc.plotId) === String(plot.fid));
      if (contacts.length === 0) return 'available';

      const interredContacts = contacts.filter(pc => pc.relationshipType === 'interred');
      if (interredContacts.length > 0) {
        // Check if any interred contact is a veteran
        const hasVeteran = interredContacts.some(pc => pc.Contact?.isVeteran);
        return hasVeteran ? 'hasVeteran' : 'interred';
      }

      return 'sold';
    };

    // Helper to check if plot matches search filter
    const isPlotFiltered = (plotId) => {
      if (searchFilteredPlotIdsRef.current === null) return false; // No filter active
      return !searchFilteredPlotIdsRef.current.includes(String(plotId)) &&
             !searchFilteredPlotIdsRef.current.includes(Number(plotId));
    };

    // Create GeoJSON FeatureCollection with plot state and filter status
    const geojson = {
      type: 'FeatureCollection',
      features: validPlots.map(plot => {
        const filtered = isPlotFiltered(plot.fid);
        return {
          type: 'Feature',
          id: plot.fid,
          geometry: plot.geometry,
          properties: {
            fid: plot.fid,
            alias: plot.alias || '',
            size: plot.size || '',
            section: plot.section || '',
            statusId: plot.statusId || 0,
            isReserved: plot.isReserved || false,
            plotState: getPlotStateForRender(plot),
            isFiltered: filtered
          }
        };
      })
    };

    debug('GeoJSON FeatureCollection:', geojson);

    // Add source
    map.addSource('plots', {
      type: 'geojson',
      data: geojson
    });

    // Use configurable colors based on plot state
    // Map from context keys to expected keys with fallbacks
    const defaultColors = {
      available: '#22c55e',
      reserved: '#f59e0b',
      sold: '#3b82f6',
      interred: '#6b7280',
      hasVeteran: '#7c3aed',
      highlight: '#ef4444'
    };
    const colors = {
      available: plotColors?.noContacts || plotColors?.available || defaultColors.available,
      reserved: plotColors?.reserved || defaultColors.reserved,
      sold: plotColors?.hasContacts || plotColors?.sold || defaultColors.sold,
      interred: plotColors?.hasInterred || plotColors?.interred || defaultColors.interred,
      hasVeteran: plotColors?.hasVeteran || defaultColors.hasVeteran,
      highlight: plotColors?.highlight || defaultColors.highlight
    };

    // Add fill layer with colors based on plot state
    // Use conditional opacity: filtered plots are 90% transparent (0.1 opacity)
    map.addLayer({
      id: 'plots-fill',
      type: 'fill',
      source: 'plots',
      paint: {
        'fill-color': [
          'match',
          ['get', 'plotState'],
          'available', colors.available,
          'reserved', colors.reserved,
          'sold', colors.sold,
          'interred', colors.interred,
          'hasVeteran', colors.hasVeteran,
          colors.available // default
        ],
        'fill-opacity': [
          'case',
          ['get', 'isFiltered'],
          0.1, // 90% transparent for filtered-out plots
          0.5  // Normal opacity for visible plots
        ]
      }
    });

    // Add outline layer with conditional opacity
    map.addLayer({
      id: 'plots-outline',
      type: 'line',
      source: 'plots',
      paint: {
        'line-color': '#ffffff',
        'line-width': 2,
        'line-opacity': [
          'case',
          ['get', 'isFiltered'],
          0.2, // Faded outline for filtered-out plots
          1    // Full opacity for visible plots
        ]
      }
    });

    // Add labels layer with conditional opacity
    map.addLayer({
      id: 'plots-labels',
      type: 'symbol',
      source: 'plots',
      layout: {
        'text-field': ['get', 'alias'],
        'text-size': 12,
        'text-anchor': 'center'
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 1,
        'text-opacity': [
          'case',
          ['get', 'isFiltered'],
          0.2, // Faded label for filtered-out plots
          1    // Full opacity for visible plots
        ]
      }
    });
  }, [map, plots, allPlotContacts, plotColors]);

  // Update map filter directly without re-rendering React components
  // This avoids re-creating the DataTable when searching
  const updateMapFilter = useCallback((filteredPlotIds) => {
    searchFilteredPlotIdsRef.current = filteredPlotIds;

    if (!map) return;

    const source = map.getSource('plots');
    if (!source) return;

    // Helper to check if plot matches search filter
    const isPlotFiltered = (plotId) => {
      if (filteredPlotIds === null) return false;
      return !filteredPlotIds.includes(String(plotId)) &&
             !filteredPlotIds.includes(Number(plotId));
    };

    // Helper to get plot state based on contacts and reserved status
    const getPlotStateForRender = (plot) => {
      if (plot.isReserved) return 'reserved';
      const contacts = allPlotContacts.filter(pc => String(pc.plotId) === String(plot.fid));
      if (contacts.length === 0) return 'available';
      const interredContacts = contacts.filter(pc => pc.relationshipType === 'interred');
      if (interredContacts.length > 0) {
        const hasVeteran = interredContacts.some(pc => pc.Contact?.isVeteran);
        return hasVeteran ? 'hasVeteran' : 'interred';
      }
      return 'sold';
    };

    // Filter valid plots
    const validPlots = plotsRef.current.filter(plot =>
      plot.geometry && plot.geometry.type && plot.geometry.coordinates &&
      Array.isArray(plot.geometry.coordinates) && plot.geometry.coordinates.length > 0
    );

    // Build updated GeoJSON
    const geojson = {
      type: 'FeatureCollection',
      features: validPlots.map(plot => ({
        type: 'Feature',
        id: plot.fid,
        geometry: plot.geometry,
        properties: {
          fid: plot.fid,
          alias: plot.alias || '',
          size: plot.size || '',
          section: plot.section || '',
          statusId: plot.statusId || 0,
          isReserved: plot.isReserved || false,
          plotState: getPlotStateForRender(plot),
          isFiltered: isPlotFiltered(plot.fid)
        }
      }))
    };

    // Update the source data directly
    source.setData(geojson);
  }, [map, allPlotContacts]);

  // Render polygons when map is ready and plots are loaded
  useEffect(() => {
    if (map && mapReady && plots.length > 0) {
      renderPolygons();

      // Move highlight layers to top if they exist (they may have been hidden by re-rendered plot layers)
      if (map.getLayer('plot-highlight-fill')) {
        map.moveLayer('plot-highlight-fill');
      }
      if (map.getLayer('plot-highlight-outline')) {
        map.moveLayer('plot-highlight-outline');
      }

      // Only zoom to all plots if we haven't handled a highlight
      if (!hasHandledHighlightRef.current) {
        const highlightParam = searchParams.get('highlight');
        if (!highlightParam) {
          zoomToPlots();
        }
      }
    }
  }, [map, mapReady, plots, renderPolygons, zoomToPlots, searchParams]);

  // Handle highlight parameter from URL (e.g., from "View on Map" buttons)
  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (highlightParam && map && mapReady && plots.length > 0 && !hasHandledHighlightRef.current) {
      debug('Highlight parameter detected:', highlightParam);
      hasHandledHighlightRef.current = true; // Mark as handled before any state updates
      setHighlightedPlotId(highlightParam);
      zoomToPlot(highlightParam);

      // Clear the highlight parameter from URL after processing
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('highlight');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, map, mapReady, plots, zoomToPlot, setSearchParams]);

  // Apply visual highlight effect to the highlighted plot
  useEffect(() => {
    if (!map || !mapReady || !highlightedPlotId) {
      debug('Highlight effect skipped:', { map: !!map, mapReady, highlightedPlotId });
      return;
    }

    debug('Applying highlight to plot:', highlightedPlotId);

    // Remove existing highlight layers if present
    if (map.getLayer('plot-highlight-fill')) {
      map.removeLayer('plot-highlight-fill');
    }
    if (map.getLayer('plot-highlight-outline')) {
      map.removeLayer('plot-highlight-outline');
    }
    if (map.getSource('plot-highlight')) {
      map.removeSource('plot-highlight');
    }

    const plot = plots.find(p => String(p.fid) === String(highlightedPlotId));
    if (!plot || !plot.geometry) {
      debug('Plot not found for highlight:', highlightedPlotId);
      return;
    }

    debug('Found plot for highlight:', plot.alias);

    const highlightColor = plotColors?.highlight || '#fbbf24';

    // Add highlight source and layer
    map.addSource('plot-highlight', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: plot.geometry,
        properties: {}
      }
    });

    // Add highlight fill layer on top of other layers
    map.addLayer({
      id: 'plot-highlight-fill',
      type: 'fill',
      source: 'plot-highlight',
      paint: {
        'fill-color': highlightColor,
        'fill-opacity': 0.7
      }
    });

    // Add highlight outline for better visibility
    map.addLayer({
      id: 'plot-highlight-outline',
      type: 'line',
      source: 'plot-highlight',
      paint: {
        'line-color': highlightColor,
        'line-width': 4,
        'line-opacity': 1
      }
    });

    debug('Highlight layer added successfully');

    // Clear highlight after a delay
    const timeout = setTimeout(() => {
      setHighlightedPlotId(null);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [map, mapReady, highlightedPlotId, plots, plotColors]);

  // Clean up highlight layer when highlightedPlotId is cleared
  useEffect(() => {
    if (!map || highlightedPlotId) return;

    if (map.getLayer('plot-highlight-fill')) {
      map.removeLayer('plot-highlight-fill');
    }
    if (map.getLayer('plot-highlight-outline')) {
      map.removeLayer('plot-highlight-outline');
    }
    if (map.getSource('plot-highlight')) {
      map.removeSource('plot-highlight');
    }
  }, [map, highlightedPlotId]);

  // Handle map move events for sync
  useEffect(() => {
    if (map) {
      const handleMoveEnd = () => {
        if (syncWithMap) setMapBounds(map.getBounds());
      };
      map.on('moveend', handleMoveEnd);
      return () => {
        map.off('moveend', handleMoveEnd);
      };
    }
  }, [map, syncWithMap]);

  // Persist syncWithMap to localStorage and trigger immediate sync when enabled
  useEffect(() => {
    localStorage.setItem('plotMapSyncWithMap', JSON.stringify(syncWithMap));

    // When sync is enabled, immediately update map bounds
    if (syncWithMap && map) {
      setMapBounds(map.getBounds());
    }

    // When sync is disabled, clear the search filter to show all plots on map
    if (!syncWithMap) {
      updateMapFilter(null);
    }
  }, [syncWithMap, map, updateMapFilter]);

  // Initialize map
  useEffect(() => {
    const mapboxMap = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: [-83.1174813, 33.8791535],
      zoom: 17,
      boxZoom: false,
      ...(!previewMode && { cooperativeGestures: true }),
    });

    mapRef.current = mapboxMap;

    if (previewMode) {
      mapboxMap.dragPan.disable();
      mapboxMap.scrollZoom.disable();
      mapboxMap.doubleClickZoom.disable();
      mapboxMap.touchZoomRotate.disable();

      // Apply touch-action CSS to canvas for mobile scrolling
      mapboxMap.on('load', () => {
        const canvas = mapboxMap.getCanvas();
        if (canvas) {
          canvas.style.touchAction = 'pan-y';
        }

        mapboxMap.on('click', () => {
          window.location.href = '/plots';
        });
      });
    } else {
      mapboxMap.dragRotate.disable();
      mapboxMap.touchZoomRotate.disableRotation();
      mapboxMap.scrollZoom.disable();

      mapboxMap.on('wheel', (e) => {
        if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) {
          mapboxMap.scrollZoom.enable();
        } else {
          mapboxMap.scrollZoom.disable();
        }
      });

      // Initialize Mapbox Draw
      const drawControl = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        },
        defaultMode: 'simple_select'
      });

      mapboxMap.on('load', () => {
        mapboxMap.addControl(new mapboxgl.NavigationControl(), 'top-left');
        mapboxMap.addControl(new mapboxgl.FullscreenControl(), 'top-right');

        // Custom Home control to zoom to all plots
        class HomeControl {
          onAdd(map) {
            this._map = map;
            this._container = document.createElement('div');
            this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
            this._container.innerHTML = `
              <button type="button" title="Zoom to all plots" aria-label="Zoom to all plots" class="mapboxgl-ctrl-icon" style="display: flex; align-items: center; justify-content: center;">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style="width: 18px; height: 18px;">
                  <path fill-rule="evenodd" d="M9.293 2.293a1 1 0 011.414 0l7 7A1 1 0 0117 11h-1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1H9a1 1 0 00-1 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-6H3a1 1 0 01-.707-1.707l7-7z" clip-rule="evenodd" />
                </svg>
              </button>
            `;
            this._container.querySelector('button').addEventListener('click', () => {
              // Zoom to all plots - trigger the zoomToPlots effect
              if (plotsRef.current.length > 0) {
                const bounds = new mapboxgl.LngLatBounds();
                plotsRef.current.forEach(plot => {
                  if (plot.geometry && plot.geometry.coordinates) {
                    const coords = plot.geometry.coordinates;
                    if (plot.geometry.type === 'MultiPolygon') {
                      coords.forEach(polygon => {
                        polygon.forEach(ring => {
                          ring.forEach(coord => bounds.extend(coord));
                        });
                      });
                    } else if (plot.geometry.type === 'Polygon') {
                      coords.forEach(ring => {
                        ring.forEach(coord => bounds.extend(coord));
                      });
                    }
                  }
                });
                if (!bounds.isEmpty()) {
                  this._map.fitBounds(bounds, { padding: 100, duration: 800 });
                }
              }
            });
            return this._container;
          }
          onRemove() {
            this._container.parentNode.removeChild(this._container);
            this._map = undefined;
          }
        }
        mapboxMap.addControl(new HomeControl(), 'top-left');

        mapboxMap.addControl(drawControl, 'top-left');
        setDraw(drawControl);

        // Handle polygon creation
        mapboxMap.on('draw.create', (e) => {
          debug('Draw create:', e);
          const feature = e.features[0];
          if (feature) {
            setPendingGeometry(feature.geometry);
            setCurrentPlot({ alias: '', section: '', statusId: 0 });
            setFormConfig({
              apiEndpoint: '/api/plots',
              fields: [
                { name: 'alias', label: 'Alias', type: 'text', required: true },
                { name: 'section', label: 'Section', type: 'text', required: false },
                { name: 'isReserved', label: 'Reserved', type: 'checkbox', required: false }
              ],
            });
            setIsOverlayOpen(true);
            setIsDrawingMode(false);
          }
        });

        // Handle polygon update (editing vertices)
        mapboxMap.on('draw.update', async (e) => {
          debug('Draw update:', e);
          const feature = e.features[0];
          if (feature && feature.id) {
            // Find the plot being edited using ref for current value
            const plotId = feature.id;
            const plot = plotsRef.current.find(p => String(p.fid) === String(plotId));
            if (plot) {
              try {
                const response = await fetch(`/api/plots/${plot.fid}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ geometry: feature.geometry }),
                });
                if (response.ok) {
                  const updatedPlot = await response.json();
                  setPlots((prev) =>
                    prev.map((p) => (p.fid === updatedPlot.fid ? updatedPlot : p))
                  );
                }
              } catch (error) {
                console.error('Error updating plot geometry:', error);
              }
            }
          }
        });

        // Shared popup logic for both click and touch-tap
        async function showPlotPopup(feature, lngLat) {
          const currentPlots = plotsRef.current;
          const plot = currentPlots.find(p => Number(p.fid) === Number(feature.properties.fid));
          if (!plot) {
            debug('Plot not found for fid:', feature.properties.fid);
            return;
          }

          debug('Found plot:', plot);

          // Remove existing popup
          if (popupRef.current) {
            popupRef.current.remove();
          }

          // Calculate plot size if enabled
          const plotArea = showPlotSizeRef.current && plot.geometry ? calculatePlotArea(plot.geometry) : null;
          const sizeHtml = plotArea ? `<p class="text-sm text-gray-600">Size: ~${plotArea} sq ft <span class="text-xs text-gray-400">(est.)</span></p>` : '';

          const popup = new mapboxgl.Popup({
            offset: 15,
            closeOnClick: true,
            maxWidth: '350px',
            focusAfterOpen: false
          })
            .setLngLat(lngLat)
            .setHTML(`
              <div class="p-2" id="popup-content-${plot.fid}">
                <h3 class="text-lg font-semibold mb-2">${plot.alias || 'Unnamed'}</h3>
                ${plot.section ? `<p class="text-sm">Section: ${plot.section}</p>` : ''}
                ${sizeHtml}
                <div class="my-2 text-sm text-gray-500">Loading contacts & sales...</div>
                <div class="flex space-x-2 mt-3">
                  <button id="edit-${plot.fid}" class="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-700">Edit</button>
                  <button id="manage-${plot.fid}" class="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-700">Manage</button>
                </div>
              </div>
            `)
            .addTo(mapboxMap);

          popupRef.current = popup;

          // Pan map to ensure popup is visible within bounds
          const popupElement = popup.getElement();
          if (popupElement) {
            setTimeout(() => {
              const mapContainer = mapboxMap.getContainer();
              const popupRect = popupElement.getBoundingClientRect();
              const mapRect = mapContainer.getBoundingClientRect();

              let panX = 0;
              let panY = 0;
              const padding = 20;

              if (popupRect.left < mapRect.left + padding) {
                panX = popupRect.left - mapRect.left - padding;
              } else if (popupRect.right > mapRect.right - padding) {
                panX = popupRect.right - mapRect.right + padding;
              }

              if (popupRect.top < mapRect.top + padding) {
                panY = popupRect.top - mapRect.top - padding;
              } else if (popupRect.bottom > mapRect.bottom - padding) {
                panY = popupRect.bottom - mapRect.bottom + padding;
              }

              if (panX !== 0 || panY !== 0) {
                mapboxMap.panBy([panX, panY], { duration: 300 });
              }
            }, 50);
          }

          // Fetch contacts and sales for this plot
          try {
            const [contactsRes, salesRes] = await Promise.all([
              fetch(`/api/plot-contacts/plot/${plot.fid}`),
              fetch(`/api/plot-sales/plot/${plot.fid}`)
            ]);

            const contacts = contactsRes.ok ? await contactsRes.json() : [];
            const sales = salesRes.ok ? await salesRes.json() : [];

            const popupContent = document.getElementById(`popup-content-${plot.fid}`);
            if (popupContent) {
              let contactsHtml = '';
              if (contacts.length > 0) {
                contactsHtml = `
                  <div class="mt-3 border-t pt-2">
                    <h4 class="text-sm font-semibold text-gray-700">Contacts (${contacts.length})</h4>
                    <ul class="mt-1 space-y-1">
                      ${contacts.map(pc => `
                        <li class="text-xs">
                          <span class="font-medium">${pc.Contact?.firstName || ''} ${pc.Contact?.lastName || ''}</span>
                          <span class="text-gray-500 ml-1">(${pc.relationshipType})</span>
                          ${pc.isPrimary ? '<span class="ml-1 text-green-600">Primary</span>' : ''}
                        </li>
                      `).join('')}
                    </ul>
                  </div>
                `;
              }

              let salesHtml = '';
              if (sales.length > 0) {
                salesHtml = `
                  <div class="mt-3 border-t pt-2">
                    <h4 class="text-sm font-semibold text-gray-700">Sales (${sales.length})</h4>
                    <ul class="mt-1 space-y-1">
                      ${sales.map(ps => `
                        <li class="text-xs">
                          <span class="font-medium">$${Number(ps.Sale?.saleTotalAmount || 0).toFixed(2)}</span>
                          <span class="text-gray-500 ml-1">${ps.Sale?.saleDate ? new Date(ps.Sale.saleDate).toLocaleDateString() : ''}</span>
                          ${ps.Sale?.contact ? `<span class="ml-1">- ${ps.Sale.contact.firstName} ${ps.Sale.contact.lastName}</span>` : ''}
                        </li>
                      `).join('')}
                    </ul>
                  </div>
                `;
              }

              const noDataHtml = contacts.length === 0 && sales.length === 0
                ? '<p class="mt-2 text-xs text-gray-400">No contacts or sales linked</p>'
                : '';

              popupContent.innerHTML = `
                <h3 class="text-lg font-semibold mb-2">${plot.alias || 'Unnamed'}</h3>
                ${showPlotSizeRef.current && plot.size ? `<p class="text-sm">Size: ${plot.size}</p>` : ''}
                ${plot.section ? `<p class="text-sm">Section: ${plot.section}</p>` : ''}
                ${contactsHtml}
                ${salesHtml}
                ${noDataHtml}
                <div class="flex flex-wrap gap-2 mt-3 pt-2 border-t">
                  <button id="edit-${plot.fid}" class="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-700">Edit</button>
                  <button id="manage-${plot.fid}" class="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-700">Manage</button>
                </div>
              `;

              attachPopupListeners(plot, popup);
            }
          } catch (error) {
            debug('Error fetching plot relations:', error);
          }

          attachPopupListeners(plot, popup);
        }

        // Handle click on polygons for popup (desktop)
        let touchHandledClick = false;
        mapboxMap.on('click', 'plots-fill', async (e) => {
          if (touchHandledClick) {
            touchHandledClick = false;
            return;
          }
          debug('Polygon clicked', e.features);
          const feature = e.features[0];
          if (!feature) return;
          await showPlotPopup(feature, e.lngLat);
        });

        // Mobile tap detection - bypasses unreliable click synthesis
        // when cooperativeGestures + touchAction: pan-y are active
        let touchStartInfo = null;
        const canvas = mapboxMap.getCanvas();

        canvas.addEventListener('touchstart', (e) => {
          if (e.touches.length === 1) {
            touchStartInfo = {
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
              time: Date.now()
            };
          } else {
            touchStartInfo = null;
          }
        }, { passive: true });

        canvas.addEventListener('touchend', (e) => {
          if (!touchStartInfo) return;
          const touch = e.changedTouches[0];
          const dx = touch.clientX - touchStartInfo.x;
          const dy = touch.clientY - touchStartInfo.y;
          const dt = Date.now() - touchStartInfo.time;
          touchStartInfo = null;

          // Detect tap: minimal movement (<10px), short duration (<300ms)
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && dt < 300) {
            const rect = canvas.getBoundingClientRect();
            const point = [touch.clientX - rect.left, touch.clientY - rect.top];
            const features = mapboxMap.queryRenderedFeatures(point, { layers: ['plots-fill'] });
            if (features.length > 0) {
              touchHandledClick = true;
              const lngLat = mapboxMap.unproject(point);
              showPlotPopup(features[0], lngLat);
            }
          }
        }, { passive: true });

        // Helper function to attach popup button listeners
        function attachPopupListeners(plot, popup) {
          setTimeout(() => {
            const editBtn = document.getElementById(`edit-${plot.fid}`);
            const manageBtn = document.getElementById(`manage-${plot.fid}`);

            if (editBtn) {
              editBtn.onclick = (ev) => {
                ev.stopPropagation();
                handleEdit(plot);
                popup.remove();
              };
            }
            if (manageBtn) {
              manageBtn.onclick = (ev) => {
                ev.stopPropagation();
                handleManagePlot(plot);
                popup.remove();
              };
            }
          }, 0);
        }

        // Change cursor on hover
        mapboxMap.on('mouseenter', 'plots-fill', () => {
          mapboxMap.getCanvas().style.cursor = 'pointer';
        });
        mapboxMap.on('mouseleave', 'plots-fill', () => {
          mapboxMap.getCanvas().style.cursor = '';
        });
      });
    }

    mapboxMap.on('load', () => {
      mapboxMap.setPadding({ left: 50, right: 50, top: 50, bottom: 50 });
      setMap(mapboxMap);

      // Mark map as ready once style is fully loaded
      if (mapboxMap.isStyleLoaded()) {
        debug('Map style loaded immediately');
        setMapReady(true);
      } else {
        mapboxMap.once('idle', () => {
          debug('Map idle - setting ready');
          setMapReady(true);
        });
      }
    });

    fetchPlots();
    // Initial fetch attempt (may fail if token not ready)
    fetchAllPlotContacts();

    return () => {
      mapboxMap.remove();
    };
  }, []);

  // Re-fetch plot contacts when token becomes available
  useEffect(() => {
    if (token) {
      fetchAllPlotContacts();
    }
  }, [token]);

  // DataTable effect
  useEffect(() => {
    if (!map) return;
    if (!showTable) return;

    // Save current search term before recreating table
    const previousSearchTerm = searchTermRef.current;

    const dataTable = new DataTable('#plotsTable', {
      searchable: true,
      fixedHeight: true,
      perPage: 10,
    });

    dataTableRef.current = dataTable;

    let visiblePlots = plots;
    if (syncWithMap && mapBounds && mapBounds.contains) {
      visiblePlots = plots.filter(plot => {
        if (plot.centroid && plot.centroid.coordinates) {
          return mapBounds.contains(plot.centroid.coordinates);
        }
        return true;
      });
    }

    // Update filtered count for display
    setFilteredCount(visiblePlots.length);

    // Build table headings and data based on showPlotSize setting
    // Added Owner(s) and Interred columns
    const headings = showPlotSize
      ? ["Alias", "Size (est.)", "Section", "Owner(s)", "Interred", "Actions"]
      : ["Alias", "Section", "Owner(s)", "Interred", "Actions"];

    const data = visiblePlots.map(plot => {
      const plotArea = showPlotSize && plot.geometry ? calculatePlotArea(plot.geometry) : null;
      const sizeCell = plotArea ? `~${plotArea} sq ft` : '';
      const owners = getContactNamesByType(plot.fid, ['owner', 'co-owner']);
      const interred = getContactNamesByType(plot.fid, ['interred']);
      const actionsCell = `<div class="flex gap-1">
          <button data-action="zoom" data-plot-id="${plot.fid}" class="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded" title="Zoom to plot">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L18 17l-1.41 1.41-3.05-3.05a7 7 0 01-9.9-9.9zm1.41 8.49a5 5 0 107.08-7.08 5 5 0 00-7.08 7.08z" clip-rule="evenodd" />
              <path d="M10 7a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1H8a1 1 0 110-2h1V8a1 1 0 011-1z" />
            </svg>
          </button>
          <button data-action="edit" data-plot-id="${plot.fid}" class="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded" title="Edit plot">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>
        </div>`;

      if (showPlotSize) {
        return { fid: plot.fid, cells: [plot.alias || '', sizeCell, plot.section || '', owners, interred, actionsCell] };
      } else {
        return { fid: plot.fid, cells: [plot.alias || '', plot.section || '', owners, interred, actionsCell] };
      }
    });

    dataTable.insert({ headings, data: data.map(d => d.cells) });

    // Handle search events to zoom map and filter plots
    // We manually filter the data array since simple-datatables DOM state is unreliable
    let searchTimeout = null;
    const handleSearch = (e) => {
      // Skip if we're just restoring search after table re-render (prevents infinite loop)
      if (isRestoringSearchRef.current) {
        return;
      }

      // Clear any pending timeout
      if (searchTimeout) clearTimeout(searchTimeout);

      // Small delay to debounce rapid typing
      searchTimeout = setTimeout(() => {
        // Get search term from the input or event
        const searchInputEl = document.querySelector('.datatable-input');
        const searchTerm = searchInputEl?.value?.trim().toLowerCase() || '';

        if (!searchTerm) {
          // No search - show all plots on map (only when syncWithMap is enabled)
          searchTermRef.current = '';
          if (syncWithMap) {
            updateMapFilter(null);
          }
          return;
        }

        // Save search term to ref so it persists across re-renders
        searchTermRef.current = searchTerm;

        // Only filter map when syncWithMap is enabled
        if (!syncWithMap) {
          return;
        }

        // Manually filter the data array based on search term
        // This is more reliable than trying to read DOM state
        const matchedPlotIds = data
          .filter(row => {
            // Check if any cell (except the actions cell which is last) contains the search term
            const searchableCells = row.cells.slice(0, -1); // Exclude actions column
            return searchableCells.some(cell => {
              const cellText = String(cell).toLowerCase();
              return cellText.includes(searchTerm);
            });
          })
          .map(row => String(row.fid));

        // Update map filter directly (without React re-render)
        updateMapFilter(matchedPlotIds.length > 0 ? matchedPlotIds : []);

        // Zoom to matched plots
        if (matchedPlotIds.length > 0) {
          zoomToFilteredPlots(matchedPlotIds);
        }
      }, 500); // 500ms debounce for better typing experience
    };

    // Listen for search input changes - use DOM query to find the search input
    // simple-datatables creates the input with class 'datatable-input'
    const searchInput = document.querySelector('.datatable-input');
    if (searchInput) {
      searchInput.addEventListener('input', handleSearch);
    }

    // Also listen to the datatable's search event as a backup
    dataTable.on('datatable.search', () => {
      handleSearch();
    });

    // Restore previous search term if it exists (after table re-render)
    if (previousSearchTerm && searchInput) {
      searchInput.value = previousSearchTerm;
      // Set flag to prevent handleSearch from triggering during restoration
      isRestoringSearchRef.current = true;
      // Trigger the datatable's search to filter the table visually
      dataTable.search(previousSearchTerm);
      // Reset the flag after a short delay to allow events to complete
      setTimeout(() => {
        isRestoringSearchRef.current = false;
      }, 0);
    }

    // Attach event listeners to action buttons and row clicks
    const tableContainer = document.getElementById('plotsTable');
    const handleTableClick = (e) => {
      // Check if an action button was clicked
      const button = e.target.closest('button[data-action]');
      if (button) {
        const action = button.dataset.action;
        const plotId = button.dataset.plotId;
        const plot = plotsRef.current.find(p => String(p.fid) === String(plotId));

        if (!plot) return;

        if (action === 'zoom') {
          // Zoom to and highlight the plot
          setHighlightedPlotId(plotId);
          zoomToPlot(plotId);
        } else if (action === 'edit') {
          // Open edit modal
          handleEdit(plot);
        }
        return;
      }

      // Check if a table row was clicked (not header)
      const row = e.target.closest('tbody tr');
      if (row) {
        // Get the plot ID from the action buttons within this row
        const actionButton = row.querySelector('button[data-plot-id]');
        if (actionButton) {
          const plotId = actionButton.dataset.plotId;
          const plot = plotsRef.current.find(p => String(p.fid) === String(plotId));
          if (plot) {
            handleManagePlot(plot);
          }
        }
      }
    };

    if (tableContainer) {
      tableContainer.addEventListener('click', handleTableClick);
    }

    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
      if (searchInput) {
        searchInput.removeEventListener('input', handleSearch);
      }
      if (tableContainer) {
        tableContainer.removeEventListener('click', handleTableClick);
      }
      if (dataTable) dataTable.destroy();
      dataTableRef.current = null;
    };
  }, [plots, mapBounds, syncWithMap, map, showTable, zoomToPlot, showPlotSize, calculatePlotArea, allPlotContacts, getContactNamesByType, zoomToFilteredPlots, updateMapFilter]);

  const fetchPlots = async () => {
    try {
      const response = await fetch('/api/plots');
      const plotsData = await response.json();
      debug('Fetched plots', plotsData);
      setPlots(plotsData);
    } catch (error) {
      console.error('Error fetching plots:', error);
    }
  };

  const fetchAllPlotContacts = async () => {
    try {
      const response = await fetch('/api/plot-contacts', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const contactsData = await response.json();
        debug('Fetched all plot contacts', contactsData);
        setAllPlotContacts(contactsData);
      }
    } catch (error) {
      console.error('Error fetching plot contacts:', error);
    }
  };

  const handleEdit = (plot) => {
    // Check if plot has an owner contact
    const plotContacts = allPlotContacts.filter(pc => String(pc.plotId) === String(plot.fid));
    const hasOwner = plotContacts.some(pc => pc.relationshipType === 'owner' || pc.relationshipType === 'co-owner');

    // If plot has an owner, force isReserved to true and disable the field
    const updatedPlot = hasOwner ? { ...plot, isReserved: true } : plot;

    setCurrentPlot(updatedPlot);
    setPendingGeometry(null);
    setFormConfig({
      apiEndpoint: '/api/plots',
      fields: [
        { name: 'alias', label: 'Alias', type: 'text', required: true },
        { name: 'section', label: 'Section', type: 'text', required: false },
        {
          name: 'isReserved',
          label: 'Reserved',
          type: 'checkbox',
          required: false,
          disabled: hasOwner,
          hint: hasOwner ? 'Reserved status is locked because this plot has an owner.' : null
        }
      ],
    });
    setIsOverlayOpen(true);
  };

  const handleAdjust = (plot) => {
    if (!draw) return;

    // Clear any existing drawn features
    draw.deleteAll();

    // Add the plot's geometry to draw mode for editing
    const feature = {
      id: String(plot.fid),
      type: 'Feature',
      geometry: plot.geometry,
      properties: {
        fid: plot.fid,
        alias: plot.alias
      }
    };

    draw.add(feature);
    draw.changeMode('direct_select', { featureId: String(plot.fid) });
  };

  // Version of handleAdjust that uses ref (for use in event handlers)
  const handleAdjustWithRef = (plot) => {
    const currentDraw = drawRef.current;
    if (!currentDraw) {
      debug('Draw control not available');
      return;
    }

    // Clear any existing drawn features
    currentDraw.deleteAll();

    // Add the plot's geometry to draw mode for editing
    const feature = {
      id: String(plot.fid),
      type: 'Feature',
      geometry: plot.geometry,
      properties: {
        fid: plot.fid,
        alias: plot.alias
      }
    };

    currentDraw.add(feature);
    currentDraw.changeMode('direct_select', { featureId: String(plot.fid) });
  };

  const handleCreateNew = () => {
    if (!draw) return;
    draw.deleteAll();
    draw.changeMode('draw_polygon');
    setIsDrawingMode(true);
  };

  const handleSave = async (plotData) => {
    try {
      let endpoint = '/api/plots';
      let method = 'POST';
      let body = { ...plotData };

      if (plotData.fid) {
        // Update existing
        endpoint = `/api/plots/${plotData.fid}`;
        method = 'PUT';
      } else if (pendingGeometry) {
        // New plot with drawn geometry
        body.geometry = pendingGeometry;
      }

      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to save plot');
      }

      const savedPlot = await response.json();

      if (plotData.fid) {
        setPlots((prev) =>
          prev.map((p) => (p.fid === savedPlot.fid ? savedPlot : p))
        );
      } else {
        setPlots((prev) => [...prev, savedPlot]);
      }

      // Clear draw features
      if (draw) {
        draw.deleteAll();
      }
      setPendingGeometry(null);
      setIsOverlayOpen(false);
    } catch (error) {
      console.error('Error saving plot:', error);
    }
  };

  const handleCancel = () => {
    if (draw) {
      draw.deleteAll();
    }
    setPendingGeometry(null);
    setIsOverlayOpen(false);
    setIsDrawingMode(false);
  };

  const fetchPlotAttachments = async (plotFid) => {
    try {
      const res = await fetch(`/api/plot-attachments/${plotFid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setPlotAttachments(await res.json());
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const handleManagePlot = async (plot) => {
    setManagePlot(plot);
    setIsManageOpen(true);

    // Fetch plot contacts, sales, and attachments
    try {
      const [contactsRes, salesRes, allContactsRes, allSalesRes] = await Promise.all([
        fetch(`/api/plot-contacts/plot/${plot.fid}`),
        fetch(`/api/plot-sales/plot/${plot.fid}`),
        fetch('/api/contacts'),
        fetch('/api/sales')
      ]);

      if (contactsRes.ok) setPlotContacts(await contactsRes.json());
      if (salesRes.ok) setPlotSales(await salesRes.json());
      if (allContactsRes.ok) setAllContacts(await allContactsRes.json());
      if (allSalesRes.ok) setAllSales(await allSalesRes.json());
      fetchPlotAttachments(plot.fid);
    } catch (error) {
      console.error('Error fetching plot data:', error);
    }
  };

  const handleAddContact = async (contact) => {
    if (!managePlot || !contact) return;

    try {
      const response = await fetch('/api/plot-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          plotId: managePlot.fid,
          contactId: contact.id,
          relationshipType: relationshipType
        })
      });

      if (response.ok) {
        const newContact = await response.json();
        // Enhance with the contact data for display
        newContact.Contact = contact;
        setPlotContacts(prev => [...prev, newContact]);
        // Refetch all plot contacts to update map colors
        fetchAllPlotContacts();
        // Reset relationship type
        setRelationshipType('owner');
      }
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  };

  const handleRemoveContact = async (plotContactId) => {
    try {
      const response = await fetch(`/api/plot-contacts/${plotContactId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        setPlotContacts(prev => prev.filter(pc => pc.id !== plotContactId));
        // Refetch all plot contacts to update map colors
        fetchAllPlotContacts();
      }
    } catch (error) {
      console.error('Error removing contact:', error);
    }
  };

  const handleAddSale = async (sale) => {
    if (!managePlot || !sale) return;

    try {
      const response = await fetch('/api/plot-sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plotId: managePlot.fid,
          saleId: sale.id
        })
      });

      if (response.ok) {
        const newPlotSale = await response.json();
        // Enhance with the sale data for display
        newPlotSale.Sale = sale;
        setPlotSales(prev => [...prev, newPlotSale]);
      }
    } catch (error) {
      console.error('Error adding sale:', error);
    }
  };

  const handleRemoveSale = async (plotSaleId) => {
    try {
      const response = await fetch(`/api/plot-sales/${plotSaleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setPlotSales(prev => prev.filter(ps => ps.id !== plotSaleId));
      }
    } catch (error) {
      console.error('Error removing sale:', error);
    }
  };

  const closeManage = () => {
    setIsManageOpen(false);
    setManagePlot(null);
    setPlotContacts([]);
    setPlotSales([]);
    setRelationshipType('owner');
    // Refresh all plot contacts to update table with any changes
    fetchAllPlotContacts();
  };

  // Delete invalid plots with bad coordinates
  const handleDeleteInvalidPlots = async () => {
    if (!invalidPlots.length) return;

    setIsDeletingInvalid(true);
    try {
      const fids = invalidPlots.map(p => p.fid);
      const response = await fetch('/api/plots/delete-invalid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ fids })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete invalid plots');
      }

      // Refresh plots
      const plotsResponse = await fetch('/api/plots', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (plotsResponse.ok) {
        const data = await plotsResponse.json();
        setPlots(data);
      }

      setInvalidPlots([]);
      setShowInvalidDialog(false);
    } catch (error) {
      console.error('Error deleting invalid plots:', error);
      alert('Failed to delete invalid plots: ' + error.message);
    } finally {
      setIsDeletingInvalid(false);
    }
  };

  return (
    <div className={previewMode ? '' : 'p-4'}>
      <h1 className={previewMode ? 'hidden' : 'text-2xl font-bold p-2 pb-3'}>Map</h1>

      {/* Invalid geometry dialog for admins */}
      {showInvalidDialog && invalidPlots.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Invalid Plot Geometry Detected</h3>
            </div>

            <p className="text-gray-600 mb-4">
              <strong>{invalidPlots.length}</strong> plot{invalidPlots.length !== 1 ? 's have' : ' has'} invalid coordinates
              that cannot be displayed on the map. This typically happens when data is imported with the wrong
              coordinate system.
            </p>

            <div className="bg-gray-50 rounded p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-sm text-gray-500 mb-2">Affected plots:</p>
              <ul className="text-sm text-gray-700">
                {invalidPlots.slice(0, 10).map(p => (
                  <li key={p.fid}>ID: {p.fid} - {p.alias || 'No alias'}</li>
                ))}
                {invalidPlots.length > 10 && (
                  <li className="text-gray-500">...and {invalidPlots.length - 10} more</li>
                )}
              </ul>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowInvalidDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Dismiss
              </button>
              <button
                onClick={handleDeleteInvalidPlots}
                disabled={isDeletingInvalid}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-300"
              >
                {isDeletingInvalid ? 'Deleting...' : `Delete ${invalidPlots.length} Invalid Plot${invalidPlots.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHelp && (
        <>
          <div className="hidden md:block rounded-2xl bg-blue-50 p-4 mb-4">
            <div className="flex">
              <div className="shrink-0">
                <InformationCircleIcon aria-hidden="true" className="size-10 text-blue-400" />
              </div>
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <div className="hidden md:block text-lg text-slate-700">
                  <div className="mb-2">
                    <kbd className="kbd bg-white inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 mx-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">Cmd</kbd>+
                    <kbd className="kbd bg-white inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 mx-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">Scroll</kbd> to zoom.
                    Click <strong>Draw Polygon</strong> button to add a plot.
                  </div>
                  <div>Click on a polygon to edit, adjust vertices, or delete.</div>
                </div>
                <p className="mt-3 text-lg md:ml-6 md:mt-2">
                  <button
                    onClick={handleCreateNew}
                    className="whitespace-nowrap font-medium bg-sky-50 text-slate-700 hover:bg-sky-100 gap-x-1.5 rounded-md p-3 text-blue-900 ring-1 ring-inset ring-blue-200"
                  >
                    Draw New Plot
                    <PlusIcon className="h-6 w-6 inline-block ml-1" aria-hidden="true" />
                  </button>
                </p>
              </div>
            </div>
          </div>
          <div className="md:hidden my-10 text-lg md:ml-6 md:mt-2">
            <button
              onClick={handleCreateNew}
              className="whitespace-nowrap font-medium bg-green-50 text-slate-700 hover:bg-green-100 gap-x-1.5 rounded-md p-3 text-green-900 ring-1 ring-inset ring-green-200"
            >
              Draw New Plot
              <PlusIcon className="h-6 w-6 inline-block ml-1" aria-hidden="true" />
            </button>
          </div>
        </>
      )}

      {isDrawingMode && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
          <p className="font-bold">Drawing Mode Active</p>
          <p>Click on the map to draw polygon vertices. Double-click to finish.</p>
          <button
            onClick={handleCancel}
            className="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700"
          >
            Cancel Drawing
          </button>
        </div>
      )}

      {!previewMode && (
        <div className="md:hidden bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-sm text-blue-700">
          <p className="font-medium mb-1">Map Controls:</p>
          <ul className="text-xs space-y-0.5">
            <li>&bull; <strong>Pinch</strong> to zoom in/out</li>
            <li>&bull; <strong>Two fingers</strong> to pan the map</li>
            <li>&bull; <strong>One finger</strong> scrolls the page</li>
            <li>&bull; <strong>Tap</strong> a plot to view details</li>
          </ul>
        </div>
      )}

      <div
        id="mapContainer"
        className="overflow-hidden rounded-2xl relative"
        style={{ touchAction: 'pan-y' }}
      >
        <div
          id="map"
          style={{
            width: '100%',
            height: '500px',
            touchAction: 'pan-y'
          }}
        ></div>
        <Overlay
          open={isOverlayOpen}
          setOpen={setIsOverlayOpen}
          className="p-6"
          title={currentPlot?.fid ? 'Edit Plot' : 'Create Plot'}
        >
          <DynamicForm
            config={formConfig}
            initialValues={currentPlot}
            onSave={handleSave}
            onCancel={handleCancel}
          />
          {currentPlot?.fid && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Plot Actions</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsOverlayOpen(false);
                    handleAdjust(currentPlot);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-yellow-50 px-3 py-2 text-sm font-semibold text-yellow-700 shadow-sm hover:bg-yellow-100 ring-1 ring-inset ring-yellow-200"
                >
                  <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                  Adjust Geometry
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsOverlayOpen(false);
                    handleManagePlot(currentPlot);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-green-50 px-3 py-2 text-sm font-semibold text-green-700 shadow-sm hover:bg-green-100 ring-1 ring-inset ring-green-200"
                >
                  <Cog6ToothIcon className="h-4 w-4" aria-hidden="true" />
                  Manage
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsOverlayOpen(false);
                    handleDelete(currentPlot);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-100 ring-1 ring-inset ring-red-200"
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </Overlay>

        {/* Manage Plot Relationships Overlay */}
        <Overlay
          open={isManageOpen}
          setOpen={setIsManageOpen}
          className="p-6"
          title={`Manage Plot: ${managePlot?.alias || 'Unnamed'}`}
        >
          {managePlot && (
            <div className="space-y-6">
              {/* Contacts Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Contacts</h3>

                {/* Add Contact Buttons by Relationship Type */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Add contact as:</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => { setRelationshipType('owner'); setIsContactModalOpen(true); }}
                      className="inline-flex items-center rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 ring-1 ring-inset ring-blue-200"
                    >
                      <UserIcon className="h-4 w-4 mr-1.5" />
                      Owner
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRelationshipType('co-owner'); setIsContactModalOpen(true); }}
                      className="inline-flex items-center rounded-md bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 ring-1 ring-inset ring-indigo-200"
                    >
                      <UserIcon className="h-4 w-4 mr-1.5" />
                      Co-owner
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRelationshipType('interred'); setIsContactModalOpen(true); }}
                      className="inline-flex items-center rounded-md bg-purple-50 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100 ring-1 ring-inset ring-purple-200"
                    >
                      <UserIcon className="h-4 w-4 mr-1.5" />
                      Interred
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRelationshipType('family'); setIsContactModalOpen(true); }}
                      className="inline-flex items-center rounded-md bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100 ring-1 ring-inset ring-green-200"
                    >
                      <UserIcon className="h-4 w-4 mr-1.5" />
                      Family
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRelationshipType('other'); setIsContactModalOpen(true); }}
                      className="inline-flex items-center rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 ring-1 ring-inset ring-gray-200"
                    >
                      <UserIcon className="h-4 w-4 mr-1.5" />
                      Other
                    </button>
                  </div>
                </div>

                {/* Contact List */}
                {plotContacts.length > 0 ? (
                  <ul className="divide-y divide-gray-200 border rounded-md">
                    {plotContacts.map(pc => (
                      <li key={pc.id} className="flex items-center justify-between p-3">
                        <div>
                          <span className="font-medium">
                            {pc.Contact?.firstName} {pc.Contact?.lastName}
                          </span>
                          <span className="ml-2 text-sm text-gray-500">({pc.relationshipType})</span>
                          {pc.isPrimary && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                              Primary
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveContact(pc.id)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No contacts linked to this plot</p>
                )}
              </div>

              {/* Sales Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Sales</h3>

                {/* Add Sale Form */}
                <div className="mb-3">
                  <button
                    type="button"
                    onClick={() => setIsSaleModalOpen(true)}
                    className="w-full flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left hover:bg-gray-50 text-sm"
                  >
                    <span className="flex items-center text-gray-500">
                      <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                      Search and select a sale...
                    </span>
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <p className="text-xs text-gray-500 mt-1">Click to search sales by contact, products, date, or price</p>
                </div>

                {/* Sales List */}
                {plotSales.length > 0 ? (
                  <ul className="divide-y divide-gray-200 border rounded-md">
                    {plotSales.map(ps => (
                      <li key={ps.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-semibold text-gray-900">
                                ${Number(ps.Sale?.total || ps.Sale?.saleTotalAmount || 0).toFixed(2)}
                              </span>
                              <span className="text-sm text-gray-500">
                                {ps.Sale?.createdAt ? new Date(ps.Sale.createdAt).toLocaleDateString() :
                                 ps.Sale?.saleDate ? new Date(ps.Sale.saleDate).toLocaleDateString() : ''}
                              </span>
                            </div>
                            {ps.Sale?.contact && (
                              <div className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">Contact:</span> {ps.Sale.contact.firstName} {ps.Sale.contact.lastName}
                              </div>
                            )}
                            {ps.Sale?.items && ps.Sale.items.length > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                {ps.Sale.items.slice(0, 2).map((item, idx) => (
                                  <span key={idx}>
                                    {idx > 0 && ', '}
                                    {item.name} x{item.qty}
                                  </span>
                                ))}
                                {ps.Sale.items.length > 2 && (
                                  <span className="text-gray-400"> +{ps.Sale.items.length - 2} more</span>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveSale(ps.id)}
                            className="text-red-500 hover:text-red-700 text-sm ml-3"
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No sales linked to this plot</p>
                )}
              </div>

              {/* Attachments Section */}
              <div>
                <h3 className="text-lg font-semibold mb-1">Files & Media</h3>
                <PlotAttachments
                  plotId={managePlot.fid}
                  attachments={plotAttachments}
                  onRefresh={() => fetchPlotAttachments(managePlot.fid)}
                />
              </div>

              {/* Custom Fields Section */}
              <FlexFieldValues entityType="plot" entityId={String(managePlot.fid)} />

              {/* Documents Section */}
              <div>
                <h3 className="text-lg font-semibold mb-1">Documents</h3>
                <DocumentGenerationPanel
                  entityType="plot"
                  entityId={managePlot.fid}
                  compact={true}
                />
              </div>

              {/* Close Button */}
              <div className="pt-4 border-t">
                <button
                  onClick={closeManage}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </Overlay>
      </div>

      {showTable && (
        <>
          <div className="flex items-center justify-between my-4">
            <Toggle
              enabled={syncWithMap}
              setEnabled={setSyncWithMap}
              label={syncWithMap ? 'Disable Map Sync' : 'Enable Map Sync'}
            />
            <div className="text-sm text-gray-600">
              {syncWithMap ? (
                <span>
                  Showing <strong>{filteredCount}</strong> of <strong>{plots.length}</strong> plots in view
                </span>
              ) : (
                <span>
                  Total: <strong>{plots.length}</strong> plots
                </span>
              )}
            </div>
          </div>
          <style>{`
            #plotsTable tbody tr {
              cursor: pointer;
            }
            #plotsTable tbody tr:hover {
              background-color: rgba(59, 130, 246, 0.1);
            }
          `}</style>
          <table id="plotsTable" className="display dataTable">
            <thead>
              <tr>
                <th>Alias</th>
                {showPlotSize && <th>Size (est.)</th>}
                <th>Section</th>
                <th>Owner(s)</th>
                <th>Interred</th>
                <th>Actions</th>
              </tr>
            </thead>
          </table>
        </>
      )}

      {/* Contact Search Modal for adding contacts to plot */}
      <ContactSearchModal
        open={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSelect={handleAddContact}
        excludeIds={plotContacts.map(pc => pc.contactId)}
      />

      {/* Sale Search Modal for adding sales to plot */}
      <SaleSearchModal
        open={isSaleModalOpen}
        onClose={() => setIsSaleModalOpen(false)}
        onSelect={handleAddSale}
        excludeIds={plotSales.map(ps => ps.saleId)}
      />
    </div>
  );
};

export default PlotMap;
